import { createTheme } from '@mui/material/styles';
import { Accordion, AccordionDetails, AccordionSummary, Alert, CircularProgress, Container, Grid, List, ListItem, ListItemText, Paper, Snackbar, Typography } from "@mui/material";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import IPaciente from "../../../types/Paciente";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import IAlert from "../../../types/Alert";
import * as constantes from "../../../common/Constantes";
import PacienteService from '../../../services/PacienteService';
import IListaEspera from '../../../types/ListaEspera';
import IActividad from '../../../types/Actividad';
import ConfigUtils from '../../../utils/Config';
import ClaseUtils from '../../../utils/Clase';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type ClasesProps = {
    paciente: IPaciente
}

const Clases = (props: ClasesProps) => {

    const theme = createTheme();
    const history = useNavigate();

    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState(true);
    const [display, setDisplay] = useState(false);
    const [actividades] = useState<Array<IActividad>>(ConfigUtils.getActividades());
    
    const [listaEspera, setListaEspera] = useState<IListaEspera>();

    const [clases, setClases] = useState<{
      past: any[];
      present: any[];
      future: any[];
      sueltas: any[];
    }>({
      past: [],
      present: [],
      future: [],
      sueltas: []
    });

    useEffect(() => {

        PacienteService.getClases(props.paciente._id)
        .then((response: any) => {
             setClases({
               past: response.data.past,
               present: response.data.present,
               future: response.data.future,
               sueltas: response.data.sueltas
             });
             setListaEspera(response.data.listaEspera);
             if (
               response.data.past.length > 0 ||
               response.data.present.length > 0 ||
               response.data.future.length > 0 ||
               response.data.sueltas.length > 0 ||
               response.data.listaEspera
             ) {
               setDisplay(true);
             }
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'Clases.Component.useEffect', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setLoading(false);
        });

    }, [setLoading, setAlert, history, props.paciente]);

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    try {
        return (
            <div style={{ display: display ? "block" : "none" }}>
                <Grid item md={12} xs={12}>
                    <Container maxWidth="md">
                        <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', marginLeft: '10px' }} >CLASES</Typography>

                            {loading ? 
                            <Grid container>
                                <CircularProgress style={{ margin: '10px auto' }} />
                            </Grid>
                             : <div>
                                    <List dense>
                                      {["present", "future", "past", "sueltas"].map(key => {
                                        const lista = (clases as any)[key];
                                        if (!lista || lista.length === 0) return null;                                      

                                        const titulo =
                                          key === "present" ? "Clases activas" :
                                          key === "future"  ? "Clases futuras" :
                                          key === "past"    ? "Clases pasadas" :
                                                              "Clases sueltas";                                     

                                        const defaultExpanded = key === "present";                                      

                                        // --- Agrupar por año si son pasadas
                                        const agrupadoPorAnios = key === "past"
                                          ? lista.reduce((acc: any, clase: any) => {
                                              const year = new Date(clase.start).getFullYear();
                                              if (!acc[year]) acc[year] = [];
                                              acc[year].push(clase);
                                              return acc;
                                            }, {})
                                          : null;                                     

                                        return (
                                          <Accordion key={key} defaultExpanded={defaultExpanded}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                                                {titulo} ({lista.length})
                                              </Typography>
                                            </AccordionSummary>                                     

                                            <AccordionDetails style={{ display: "block" }}>
                                              {/* Clases sueltas */}
                                              {key === "sueltas" && lista.map((clase: any) => {
                                                const actividadTitle =
                                                  actividades.find(a => a.internalID == clase.title)?.title || "Actividad";
                                                const fechaStart = new Date(clase.start);
                                                const hora = fechaStart.getHours().toString().padStart(2, "0");
                                                const minutos = fechaStart.getMinutes().toString().padStart(2, "0");
                                                const diaSemana = constantes.weekNames[(fechaStart.getDay() + 6) % 7];                                      

                                                return (
                                                  <ListItem key={clase._id}>
                                                    <ListItemText
                                                      primary={`${actividadTitle} - ${diaSemana} a las ${hora}:${minutos}`}
                                                      secondary={`Clase única: ${ClaseUtils.formatUTCDate(clase.start)}`}
                                                    />
                                                  </ListItem>
                                                );
                                              })}                                     

                                              {/* Clases activas o futuras */}
                                              {key !== "sueltas" && key !== "past" && Object.values(lista.reduce((acc: any, clase: any) => {
                                                const actividadTitle =
                                                  actividades.find(a => a.internalID == clase.title)?.title || "Actividad";                                     

                                                const fechaStart = new Date(clase.start);
                                                const hora = fechaStart.getHours().toString().padStart(2, "0");
                                                const minutos = fechaStart.getMinutes().toString().padStart(2, "0");                                      

                                                const final = clase.paciente?.end;
                                                const keyGroup = `${actividadTitle}-${hora}:${minutos}-${final}`;
                                                if (!acc[keyGroup]) acc[keyGroup] = [];
                                                acc[keyGroup].push(clase);                                      

                                                return acc;
                                                }, {}))
                                                .sort((grupoA: any, grupoB: any) => {
                                                  const claseA = grupoA[0];
                                                  const claseB = grupoB[0];

                                                  const actA = actividades.find(a => a.internalID == claseA.title)?.title || "Actividad";
                                                  const actB = actividades.find(a => a.internalID == claseB.title)?.title || "Actividad";

                                                  if (actA < actB) return -1;
                                                  if (actA > actB) return 1;

                                                  // si la actividad es igual, ordenar por el primer día de la semana
                                                  const diasA = Array.isArray(claseA.master?.weekDays) ? claseA.master.weekDays : [claseA.weekDay];
                                                  const diasB = Array.isArray(claseB.master?.weekDays) ? claseB.master.weekDays : [claseB.weekDay];
                                                  const diaA = Math.min(...diasA);
                                                  const diaB = Math.min(...diasB);

                                                  if (diaA < diaB) return -1;
                                                  if (diaA > diaB) return 1;

                                                  // Ordenar por hora de inicio
                                                  const horaA = new Date(claseA.start).getHours();
                                                  const horaB = new Date(claseB.start).getHours();
                                                  if (horaA < horaB) return -1;
                                                  if (horaA > horaB) return 1;
                                                  const minA = new Date(claseA.start).getMinutes();
                                                  const minB = new Date(claseB.start).getMinutes();
                                                  return minA - minB;
                                                })
                                                .map((grupo: any, idx) => {
                                                  const claseRef = grupo[0];
                                                  const actividadTitle =
                                                    actividades.find(a => a.internalID == claseRef.title)?.title || "Actividad";                                      
                                                  
                                                  const fechaStart = new Date(claseRef.start);
                                                  const hora = fechaStart.getHours().toString().padStart(2, "0");
                                                  const minutos = fechaStart.getMinutes().toString().padStart(2, "0");                                      
                                                  
                                                  const diasSet = new Set<number>();
                                                grupo.forEach((clase: any) => {
                                                  if (Array.isArray(clase.master?.weekDays)) {
                                                    clase.master.weekDays.forEach((id: number) => diasSet.add(id));
                                                  } else {
                                                    diasSet.add(clase.weekDay);
                                                  }
                                                });
                                                const dias = Array.from(diasSet)
                                                  .sort((a, b) => a - b)
                                                  .map(id => constantes.weekNames[id])
                                                  .join(", ");                                      

                                                const starts = grupo.map((c: any) => c.paciente?.start || c.start);
                                                const ends   = grupo.map((c: any) => c.paciente?.end   || c.end   || c.start);                                     

                                                const minStart = new Date(Math.min(...starts.map((d: any) => new Date(d).getTime())));
                                                const maxEnd   = new Date(Math.max(...ends.map((d: any) => new Date(d).getTime())));                                     

                                                const estadoTexto = `Desde ${ClaseUtils.formatUTCDate(minStart)} hasta ${ClaseUtils.formatUTCDate(maxEnd)}`;                                      

                                                return (
                                                  <ListItem key={idx}>
                                                    <ListItemText
                                                      primary={`${actividadTitle} - ${dias} a las ${hora}:${minutos}`}
                                                      secondary={estadoTexto}
                                                    />
                                                  </ListItem>
                                                );
                                              })}                                     

                                              {/* Clases pasadas agrupadas por año */}
                                              {key === "past" && Object.entries(agrupadoPorAnios!)
                                                // ordenar años descendente
                                                .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                                                .map(([year, clasesYear]) => (
                                                  <Accordion key={year} style={{ margin: "8px 0" }}>
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                      <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                                        {year} ({(clasesYear as any[]).length})
                                                      </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails style={{ display: "block" }}>
                                                      {Object.values(
                                                        (clasesYear as any[]).reduce<Record<string, any[]>>((acc, clase: any) => {
                                                          const actividadTitle =
                                                            actividades.find(a => a.internalID == clase.title)?.title || "Actividad";
                                                        
                                                          const fechaStart = new Date(clase.start);
                                                          const hora = fechaStart.getHours().toString().padStart(2, "0");
                                                          const minutos = fechaStart.getMinutes().toString().padStart(2, "0");
                                                        
                                                          const final = clase.paciente?.end;
                                                          const keyGroup = `${actividadTitle}-${hora}:${minutos}-${final}`;
                                                          if (!acc[keyGroup]) acc[keyGroup] = [];
                                                          acc[keyGroup].push(clase);
                                                        
                                                          return acc;
                                                        }, {})
                                                      )
                                                        // ordenar grupos dentro del año por fecha más próxima
                                                        .sort((grupoA, grupoB) => {
                                                          const minA = Math.min(...grupoA.map(c => new Date(c.start).getTime()));
                                                          const minB = Math.min(...grupoB.map(c => new Date(c.start).getTime()));
                                                          return minB - minA; // descendente → más reciente primero
                                                        })
                                                        .map((grupo, idx) => {
                                                          const claseRef = grupo[0];
                                                          const actividadTitle =
                                                            actividades.find(a => a.internalID == claseRef.title)?.title || "Actividad";
                                                        
                                                          const fechaStart = new Date(claseRef.start);
                                                          const hora = fechaStart.getHours().toString().padStart(2, "0");
                                                          const minutos = fechaStart.getMinutes().toString().padStart(2, "0");
                                                        
                                                          const diasSet = new Set<number>();
                                                          grupo.forEach(clase => {
                                                            if (Array.isArray(clase.master?.weekDays)) {
                                                              clase.master.weekDays.forEach((id: number) => diasSet.add(id));
                                                            } else {
                                                              diasSet.add(clase.weekDay);
                                                            }
                                                          });
                                                          const dias = Array.from(diasSet)
                                                            .sort((a, b) => a - b)
                                                            .map(id => constantes.weekNames[id])
                                                            .join(", ");
                                                        
                                                          const starts = grupo.map(c => c.paciente?.start || c.start);
                                                          const ends   = grupo.map(c => c.paciente?.end   || c.end   || c.start);
                                                        
                                                          const minStart = new Date(Math.min(...starts.map(d => new Date(d).getTime())));
                                                          const maxEnd   = new Date(Math.max(...ends.map(d => new Date(d).getTime())));
                                                        
                                                          const estadoTexto = `Desde ${ClaseUtils.formatUTCDate(minStart)} hasta ${ClaseUtils.formatUTCDate(maxEnd)}`;
                                                        
                                                          return (
                                                            <ListItem key={idx}>
                                                              <ListItemText
                                                                primary={`${actividadTitle} - ${dias} a las ${hora}:${minutos}`}
                                                                secondary={estadoTexto}
                                                              />
                                                            </ListItem>
                                                          );
                                                        })}
                                                    </AccordionDetails>
                                                  </Accordion>
                                                ))}
                                            </AccordionDetails>
                                          </Accordion>
                                        );
                                      })}

                                      {/* Lista de espera como accordion colapsado */}
                                      {listaEspera && (
                                        <Accordion key="listaEspera" defaultExpanded={false}>
                                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                                              Lista de espera
                                            </Typography>
                                          </AccordionSummary>
                                    
                                          <AccordionDetails>
                                            <ListItem>
                                              <ListItemText
                                                primary={listaEspera.actividadIds.length === 1 ? "Actividad: " : "Actividades: "}
                                                secondary={listaEspera.actividadIds
                                                  .map(id => {
                                                    const actividad = actividades.find(a => a.internalID == id.toString());
                                                    return actividad ? actividad.title : null;
                                                  })
                                                  .filter(title => title !== null)
                                                  .join(", ")}
                                              />
                                            </ListItem>
                                            <ListItem>
                                              <ListItemText
                                                primary={listaEspera.weekdays.length === 1 ? "Día: " : "Días: "}
                                                secondary={`Los ${listaEspera.weekdays
                                                  .sort((a, b) => a - b)
                                                  .map(id => constantes.weekNames[id])
                                                  .join(", ")}`}
                                              />
                                            </ListItem>
                                            <ListItem>
                                              <ListItemText
                                                primary="Horario: "
                                                secondary={constantes.horarioClases[listaEspera.horarioId]}
                                              />
                                            </ListItem>
                                            {listaEspera.comentarios !== "" && (
                                              <ListItem>
                                                <ListItemText
                                                  primary="Comentarios: "
                                                  secondary={listaEspera.comentarios}
                                                />
                                              </ListItem>
                                            )}
                                          </AccordionDetails>
                                        </Accordion>
                                      )}
                                    </List>
                               </div>
                            }
                        </Paper>
                    </Container>
                </Grid>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </div>
        );

    } catch (e: any) {
        const error: ILog = { method: 'Clases.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default Clases;